import { useState, useEffect } from "react";
import { supabase } from "../../../../../integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../../ui/card";
import { Button } from "../../../../ui/button";
import { Switch } from "../../../../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../ui/select";
import { Label } from "../../../../ui/label";
import { Input } from "../../../../ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../ui/table";
import { Badge } from "../../../../ui/badge";
import { RefreshCw, CheckCircle2, XCircle, Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

interface DailySummary {
  id: string;
  run_date: string;
  started_at: string;
  finished_at: string | null;
  total_outlets: number;
  success_count: number;
  failed_count: number;
  running_count: number;
  details: Array<{
    outlet_id: string;
    outlet_name: string;
    platform: string;
    status: string;
    message: string;
    duration_ms: number;
  }>;
}

interface WorkspaceSettings {
  id: string;
  timezone: string;
  auto_refresh_enabled: boolean;
  auto_refresh_time: string;
  notify_channel: string;
  notify_to: string | null;
}
interface SyncCenterProps {
  supabaseoutletIDs?: string[],
}


export const SyncCenter = ({ supabaseoutletIDs }: SyncCenterProps) => {
  const [summary, setSummary] = useState<DailySummary | null>(null);
  const [settings, setSettings] = useState<WorkspaceSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState<any[]>([]);

  useEffect(() => {
    loadData();
    
    // Subscribe to realtime updates on scraping_jobs
    const channel = supabase
      .channel('scraping-jobs-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'scraping_jobs'
        },
        () => {
          console.log('Scraping jobs updated, reloading...');
          loadData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const loadData = async () => {
    try {
      // Get today's date
      const today = new Date().toISOString().split('T')[0];

      // Load daily summary
      const { data: summaryData } = await supabase
        .from('scraping_daily_summary')
        .select('*')
        .eq('run_date', today)
        .maybeSingle();

      if (summaryData) {
        setSummary({
          ...summaryData,
          details: (summaryData.details as any) || []
        } as DailySummary);
      } else {
        setSummary(null);
      }

      // Load workspace settings
      const { data: settingsData } = await supabase
        .from('settings_workspace')
        .select('*')
        .maybeSingle();

      setSettings(settingsData || {
        id: '',
        timezone: 'Asia/Singapore',
        auto_refresh_enabled: true,
        auto_refresh_time: '02:00:00+08',
        notify_channel: 'none',
        notify_to: null
      });

      // Load recent jobs
      const { data: jobsData } = await supabase
        .from('scraping_jobs')
        .select('*, outlets(outlet_name, business_name, branch, data_source, verified_status, country_code)').in('outlet_id', supabaseoutletIDs)
        .order('created_at', { ascending: false })
        .limit(50);

      setJobs(jobsData || []);

    } catch (error) {
      console.error('Error loading sync center data:', error);
      toast.error('Failed to load sync center data');
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updates: Partial<WorkspaceSettings>) => {
    try {
      const { error } = await supabase
        .from('settings_workspace')
        .upsert({
          ...settings,
          ...updates,
          user_id: (await supabase.auth.getUser()).data.user?.id
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      setSettings({ ...settings, ...updates } as WorkspaceSettings);
      toast.success('Settings updated');
    } catch (error: any) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    }
  };

  const retryOutlet = async (outletId: string) => {
    try {
      const { error } = await supabase.functions.invoke('scrape-outlet', {
        body: { outlet_id: outletId }
      });

      if (error) throw error;
      toast.success('Retry initiated');
    } catch (error: any) {
      console.error('Error retrying outlet:', error);
      toast.error('Failed to retry outlet');
    }
  };

  const cancelJob = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('scraping_jobs')
        .update({
          status: 'failed',
          finished_at: new Date().toISOString(),
          message: 'Cancelled by user'
        })
        .eq('id', jobId);

      if (error) throw error;
      toast.success('Job cancelled');
      loadData();
    } catch (error: any) {
      console.error('Error cancelling job:', error);
      toast.error('Failed to cancel job');
    }
  };

  const getDuration = () => {
    if (!summary?.started_at) return 'N/A';
    if (!summary?.finished_at) return 'Running...';
    
    const start = new Date(summary.started_at).getTime();
    const end = new Date(summary.finished_at).getTime();
    const diff = end - start;
    
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    
    return `${minutes}m ${seconds}s`;
  };

  return (
    <div className="space-y-6">
      {/* Daily Status Panel */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5" />
                {summary?.started_at 
                  ? `Last Auto Refresh - ${new Date(summary.started_at).toLocaleDateString()} ${new Date(summary.started_at).toLocaleTimeString()}`
                  : "No Auto Refresh Yet"
                }
              </CardTitle>
              <CardDescription>
                {summary?.started_at && (
                  <>
                    {formatDistanceToNow(new Date(summary.started_at), { addSuffix: true })} 
                    {' • '}TZ: {settings?.timezone}
                  </>
                )}
              </CardDescription>
            </div>
            <Button onClick={loadData} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* KPIs */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">
                  {supabaseoutletIDs.length || 0} / {summary?.total_outlets || 0}
                </div>
                <p className="text-sm text-muted-foreground">Outlets Processed</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-destructive">
                  {summary?.failed_count || 0}
                </div>
                <p className="text-sm text-muted-foreground">Failed</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-blue-600">
                  {summary?.running_count || 0}
                </div>
                <p className="text-sm text-muted-foreground">In Progress</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">
                  {getDuration()}
                </div>
                <p className="text-sm text-muted-foreground">Duration</p>
              </CardContent>
            </Card>
          </div>

          {/* Jobs Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Outlet</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Message</TableHead>
                  {/* <TableHead>Source</TableHead> */}
                  <TableHead>Time</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground">
                      No scraping jobs found
                    </TableCell>
                  </TableRow>
                ) : (
                  jobs.map((job) => {
                    const COUNTRY_MAP: Record<string, { flag: string; name: string }> = {
                      'SG': { flag: '🇸🇬', name: 'Singapore' },
                      'MY': { flag: '🇲🇾', name: 'Malaysia' },
                      'ID': { flag: '🇮🇩', name: 'Indonesia' },
                      'PH': { flag: '🇵🇭', name: 'Philippines' },
                      'TH': { flag: '🇹🇭', name: 'Thailand' },
                      'VN': { flag: '🇻🇳', name: 'Vietnam' },
                      'IN': { flag: '🇮🇳', name: 'India' },
                      'US': { flag: '🇺🇸', name: 'United States' },
                      'GB': { flag: '🇬🇧', name: 'United Kingdom' },
                    };
                    const country = COUNTRY_MAP[job.outlets?.country_code || 'SG'] || { flag: '🌍', name: 'Unknown' };

                    return (
                      <TableRow key={job.id}>
                        <TableCell className="font-medium">
                          <div>
                            <div>{job.outlets?.outlet_name || 'Unknown'}</div>
                            {job.outlets?.business_name && (
                              <div className="text-xs text-muted-foreground">
                                {job.outlets.business_name}
                                {job.outlets.branch && ` (${job.outlets.branch})`}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={job.platform === 'epicreviews' ? 'destructive' : 'outline'}>
                            {job.platform === 'epicreviews' ? '🟥 EpicReviews' : 
                             job.platform === 'google_maps' ? '🗺️ Google' :
                             job.platform === 'tripadvisor' ? '✈️ TripAdvisor' :
                             job.platform === 'facebook' ? '👍 Facebook' :
                             job.platform === 'instagram' ? '📷 Instagram' :
                             job.platform === 'tiktok' ? '🎵 TikTok' :
                             job.platform}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 text-sm">
                            <span>{country.flag}</span>
                            <span className="text-muted-foreground">{country.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {(job.status === 'success' || job.status === 'completed') && (
                            <Badge className="bg-green-500">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Success
                            </Badge>
                          )}
                          {job.status === 'failed' && (
                            <Badge variant="destructive">
                              <XCircle className="h-3 w-3 mr-1" />
                              Failed
                            </Badge>
                          )}
                          {job.status === 'queued' && (
                            <Badge variant="outline">
                              <Clock className="h-3 w-3 mr-1" />
                              Queued
                            </Badge>
                          )}
                          {job.status === 'pending' && (
                            <Badge variant="secondary">
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                          {(job.status === 'running' || job.status === 'in_progress') && (
                            <Badge className="bg-blue-500">
                              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                              Running
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                          {job.message ? 
                            (job.status === 'running' || job.status === 'in_progress' ? 
                              'Fetching' : 
                              job.message.match(/(\d+)\s+reviews?/i) ? 
                                `Fetched ${job.message.match(/(\d+)\s+reviews?/i)![1]} reviews` : 
                                job.message
                            ) : '—'
                          }
                        </TableCell>
                       {/*  <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {job.source?.toUpperCase() || job.outlets?.data_source?.toUpperCase() || 'APIFY'}
                          </Badge>
                          {job.outlets?.verified_status && (
                            <CheckCircle2 className="h-3 w-3 ml-1 inline text-green-600" />
                          )}
                        </TableCell> */}
                        <TableCell className="text-sm text-muted-foreground">
                          {job.created_at && formatDistanceToNow(new Date(job.created_at), { addSuffix: true })}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {(job.status === 'running' || job.status === 'in_progress') ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => cancelJob(job.id)}
                                className="text-destructive hover:text-destructive"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => retryOutlet(job.outlet_id)}
                                disabled={job.status === 'queued'}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Settings Panel */}
      <Card>
        <CardHeader>
          <CardTitle>Auto-Refresh Settings</CardTitle>
          <CardDescription>
            Configure automatic nightly review refreshes for all outlets
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Auto-refresh @ 2:00 AM</Label>
              <p className="text-sm text-muted-foreground">
                Automatically refresh reviews every night
              </p>
            </div>
            <Switch
              checked={settings?.auto_refresh_enabled || false}
              onCheckedChange={(checked) => updateSettings({ auto_refresh_enabled: checked })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Timezone</Label>
              <Select
                value={settings?.timezone || 'Asia/Singapore'}
                onValueChange={(value) => updateSettings({ timezone: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Asia/Singapore">Asia/Singapore (GMT+8)</SelectItem>
                  <SelectItem value="America/New_York">America/New York (EST)</SelectItem>
                  <SelectItem value="America/Los_Angeles">America/Los Angeles (PST)</SelectItem>
                  <SelectItem value="Europe/London">Europe/London (GMT)</SelectItem>
                  <SelectItem value="Australia/Sydney">Australia/Sydney (AEST)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notification Channel</Label>
              <Select
                value={settings?.notify_channel || 'none'}
                onValueChange={(value) => updateSettings({ notify_channel: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {settings?.notify_channel !== 'none' && (
            <div className="space-y-2">
              <Label>
                {settings?.notify_channel === 'email' ? 'Email Address' : 'Phone Number (E.164)'}
              </Label>
              <Input
                type={settings?.notify_channel === 'email' ? 'email' : 'tel'}
                placeholder={settings?.notify_channel === 'email' ? 'you@example.com' : '+1234567890'}
                value={settings?.notify_to || ''}
                onChange={(e) => updateSettings({ notify_to: e.target.value })}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
